import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Eye, Save, Upload, Sparkles, Trash2, ExternalLink, Info, X } from "lucide-react";
import api, { formatApiError, resolveMediaUrl } from "@/lib/api";
import RichEditor from "@/components/admin/RichEditor";
import { toast } from "sonner";

const emptyBlog = {
  title: "",
  slug: "",
  excerpt: "",
  body_html: "",
  featured_image: "",
  featured_image_alt: "",
  categories: [],
  tags: [],
  seo_title: "",
  seo_description: "",
  status: "draft",
  author_name: "Novarise Works",
};

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/[\s_-]+/g, "-")
    .slice(0, 120);
}

export default function BlogEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();

  const [blog, setBlog] = useState(emptyBlog);
  const [savedId, setSavedId] = useState(id || null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [tagInput, setTagInput] = useState("");
  const [catInput, setCatInput] = useState("");
  const featuredRef = useRef();
  const autoSaveTimer = useRef();

  // Load existing
  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/blogs/${id}`);
        setBlog({ ...emptyBlog, ...data });
        setSavedId(data.id);
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/admin/blogs");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  // Auto-slug from title
  useEffect(() => {
    if (!slugTouched) setBlog((b) => ({ ...b, slug: slugify(b.title) }));
    // eslint-disable-next-line
  }, [blog.title]);

  const update = (patch) => {
    setBlog((b) => ({ ...b, ...patch }));
    setDirty(true);
  };

  // Autosave: 2s debounce, only save drafts automatically (never auto-publish)
  const doSave = useCallback(async (opts = {}) => {
    const { statusOverride, silent = false } = opts;
    const payload = { ...blog };
    if (statusOverride) payload.status = statusOverride;
    if (!payload.title.trim()) {
      if (!silent) toast.error("Add a title first");
      return { ok: false };
    }
    setSaving(true);
    try {
      let data;
      if (savedId) {
        const res = await api.put(`/admin/blogs/${savedId}`, payload);
        data = res.data;
      } else {
        const res = await api.post("/admin/blogs", payload);
        data = res.data;
        setSavedId(data.id);
        // switch URL from /new to /:id/edit without a reload
        window.history.replaceState(null, "", `/admin/blogs/${data.id}/edit`);
      }
      setBlog((b) => ({ ...b, ...data }));
      setDirty(false);
      if (!silent) toast.success(payload.status === "published" ? "Published" : "Saved");
      return { ok: true, data };
    } catch (e) {
      if (!silent) toast.error(formatApiError(e));
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [blog, savedId]);

  // Autosave (drafts) with 2s debounce
  useEffect(() => {
    if (!dirty || loading) return;
    if (!blog.title.trim()) return;
    if (blog.status !== "draft") return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => doSave({ silent: true }), 2000);
    return () => clearTimeout(autoSaveTimer.current);
    // eslint-disable-next-line
  }, [blog, dirty]);

  const publish = async () => {
    const res = await doSave({ statusOverride: "published" });
    if (res.ok) {
      setBlog((b) => ({ ...b, status: "published" }));
    }
  };

  const unpublish = async () => {
    const res = await doSave({ statusOverride: "draft" });
    if (res.ok) {
      setBlog((b) => ({ ...b, status: "draft" }));
    }
  };

  const uploadFeatured = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update({ featured_image: data.url });
      toast.success("Featured image uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeBlog = async () => {
    if (!savedId) return navigate("/admin/blogs");
    if (!window.confirm(`Delete "${blog.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blogs/${savedId}`);
      toast.success("Blog deleted");
      navigate("/admin/blogs");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addTag = () => {
    const v = tagInput.trim().toLowerCase();
    if (v && !blog.tags.includes(v)) update({ tags: [...blog.tags, v] });
    setTagInput("");
  };
  const addCat = () => {
    const v = catInput.trim();
    if (v && !blog.categories.includes(v)) update({ categories: [...blog.categories, v] });
    setCatInput("");
  };

  if (loading) {
    return <div className="p-10 text-white/60">Loading…</div>;
  }

  return (
    <div className="p-6 sm:p-10 max-w-6xl" data-testid="admin-blog-editor">
      {/* Sticky top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/admin/blogs" className="w-9 h-9 rounded-lg hover:bg-white/5 text-white/70 flex items-center justify-center">
            <ArrowLeft size={16} />
          </Link>
          <div className="min-w-0">
            <p className="overline mb-0.5">{isNew ? "New Post" : "Edit Post"}</p>
            <div className="flex items-center gap-2 text-xs text-white/45 font-mono">
              <span data-testid="save-status">{saving ? "Saving…" : dirty ? "Unsaved changes" : savedId ? "All changes saved" : "Draft"}</span>
              {blog.status === "published" && savedId && (
                <a href={`/blog/${blog.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-neon-cyan hover:underline">
                  <ExternalLink size={11} /> View live
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowPreview(true)}
            data-testid="preview-btn"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm bg-white/5 border border-white/10 hover:bg-white/10 transition"
          >
            <Eye size={14} /> Preview
          </button>
          <button
            onClick={() => doSave()}
            data-testid="save-btn"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm bg-white/5 border border-white/10 hover:bg-white/10 transition"
          >
            <Save size={14} /> Save Draft
          </button>
          {blog.status === "published" ? (
            <button
              onClick={unpublish}
              data-testid="unpublish-btn"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm bg-white/5 border border-white/10 hover:bg-white/10 transition"
            >
              Unpublish
            </button>
          ) : (
            <button
              onClick={publish}
              data-testid="publish-btn"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold bg-white text-bg-0 hover:shadow-[0_0_35px_rgba(0,240,255,0.35)] transition"
            >
              <Sparkles size={14} /> Publish
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main column */}
        <div className="lg:col-span-8 space-y-5">
          <div className="glass rounded-2xl p-5">
            <input
              data-testid="blog-title"
              value={blog.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="Blog title"
              className="w-full bg-transparent text-3xl sm:text-4xl font-extrabold tracking-tight focus:outline-none placeholder:text-white/25"
            />
            <textarea
              data-testid="blog-excerpt"
              value={blog.excerpt}
              onChange={(e) => update({ excerpt: e.target.value })}
              placeholder="Short excerpt (1–2 lines shown on the blog list)"
              rows={2}
              className="mt-4 w-full bg-transparent text-white/70 focus:outline-none placeholder:text-white/25 resize-none"
            />
          </div>

          <RichEditor
            value={blog.body_html}
            onChange={(html) => update({ body_html: html })}
            placeholder="Start writing your post…"
          />
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-4">
          {/* Featured image */}
          <Panel title="Featured image">
            {blog.featured_image ? (
              <div className="relative rounded-xl overflow-hidden border border-white/10">
                <img src={resolveMediaUrl(blog.featured_image)} alt="" className="w-full h-40 object-cover" />
                <button
                  onClick={() => update({ featured_image: "" })}
                  className="absolute top-2 right-2 w-7 h-7 rounded-md bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                  aria-label="Remove"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => featuredRef.current?.click()}
                data-testid="featured-upload"
                className="w-full h-40 rounded-xl border border-dashed border-white/15 hover:border-white/30 flex flex-col items-center justify-center gap-2 text-white/50 hover:text-white transition"
              >
                <Upload size={18} />
                <span className="text-xs">Upload image (max 8MB)</span>
              </button>
            )}
            <input
              ref={featuredRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) uploadFeatured(f);
              }}
            />
            <label className="block mt-3">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/45">Image ALT text</span>
              <input
                data-testid="featured-alt"
                value={blog.featured_image_alt}
                onChange={(e) => update({ featured_image_alt: e.target.value })}
                placeholder="Describe the image"
                className="mt-1.5 w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-sm focus:outline-none focus:border-neon-cyan/40 transition"
              />
            </label>
          </Panel>

          {/* URL / Slug */}
          <Panel title="Permalink">
            <div className="flex items-center gap-1 rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2">
              <span className="text-xs text-white/40 font-mono">/blog/</span>
              <input
                data-testid="blog-slug"
                value={blog.slug}
                onChange={(e) => { setSlugTouched(true); update({ slug: slugify(e.target.value) }); }}
                placeholder="url-slug"
                className="flex-1 bg-transparent text-sm focus:outline-none"
              />
            </div>
            <p className="mt-2 text-[11px] text-white/40 flex items-start gap-1">
              <Info size={11} className="mt-0.5 shrink-0" />
              Lowercase, hyphens only. Must be unique.
            </p>
          </Panel>

          {/* Categories & tags */}
          <Panel title="Categories">
            <TagChips items={blog.categories} onRemove={(v) => update({ categories: blog.categories.filter((x) => x !== v) })} />
            <div className="mt-2 flex gap-2">
              <input
                data-testid="cat-input"
                value={catInput}
                onChange={(e) => setCatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCat())}
                placeholder="Add category"
                className="flex-1 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-sm focus:outline-none focus:border-neon-cyan/40 transition"
              />
              <button type="button" onClick={addCat} className="px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm">Add</button>
            </div>
          </Panel>

          <Panel title="Tags">
            <TagChips items={blog.tags} onRemove={(v) => update({ tags: blog.tags.filter((x) => x !== v) })} />
            <div className="mt-2 flex gap-2">
              <input
                data-testid="tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                placeholder="Add tag"
                className="flex-1 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-sm focus:outline-none focus:border-neon-cyan/40 transition"
              />
              <button type="button" onClick={addTag} className="px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm">Add</button>
            </div>
          </Panel>

          {/* SEO */}
          <Panel title="SEO">
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/45">SEO title</span>
              <input
                data-testid="seo-title"
                value={blog.seo_title}
                onChange={(e) => update({ seo_title: e.target.value })}
                placeholder="Falls back to blog title"
                className="mt-1.5 w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-sm focus:outline-none focus:border-neon-cyan/40 transition"
              />
            </label>
            <label className="block mt-3">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/45">Meta description</span>
              <textarea
                data-testid="seo-desc"
                value={blog.seo_description}
                onChange={(e) => update({ seo_description: e.target.value })}
                rows={3}
                placeholder="150–160 chars for search results"
                className="mt-1.5 w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-sm focus:outline-none focus:border-neon-cyan/40 transition resize-none"
              />
              <div className="text-[10px] text-white/40 font-mono mt-1 text-right">{blog.seo_description.length} / 160</div>
            </label>
          </Panel>

          {savedId && (
            <button
              onClick={removeBlog}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm text-rose-300 hover:bg-rose-500/10 border border-rose-400/20 transition"
            >
              <Trash2 size={14} /> Delete this post
            </button>
          )}
        </aside>
      </div>

      {/* Preview modal */}
      {showPreview && (
        <PreviewModal blog={blog} onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="glass rounded-2xl p-4">
      <p className="overline mb-3">{title}</p>
      {children}
    </div>
  );
}

function TagChips({ items, onRemove }) {
  if (!items.length) return <p className="text-xs text-white/40">None yet.</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 border border-white/10 text-xs">
          {t}
          <button onClick={() => onRemove(t)} className="text-white/40 hover:text-white" aria-label={`Remove ${t}`}>
            <X size={11} />
          </button>
        </span>
      ))}
    </div>
  );
}

function PreviewModal({ blog, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 bg-bg-0/90 backdrop-blur overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-full flex items-start justify-center p-6" onClick={(e) => e.stopPropagation()}>
        <motion.article
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="max-w-3xl w-full glass-strong rounded-3xl overflow-hidden my-8"
        >
          <div className="flex items-center justify-between px-6 py-3 border-b border-white/10">
            <span className="overline">Preview</span>
            <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-white/10 flex items-center justify-center">
              <X size={16} />
            </button>
          </div>
          {blog.featured_image && (
            <img src={resolveMediaUrl(blog.featured_image)} alt={blog.featured_image_alt} className="w-full h-72 object-cover" />
          )}
          <div className="p-8 sm:p-10">
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">{blog.title || "Untitled"}</h1>
            {blog.excerpt && <p className="mt-4 text-white/65">{blog.excerpt}</p>}
            <div
              className="prose prose-invert max-w-none mt-8"
              dangerouslySetInnerHTML={{ __html: blog.body_html || "<p class='text-white/40'>No content yet.</p>" }}
            />
          </div>
        </motion.article>
      </div>
    </motion.div>
  );
}
